import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ReviewForm = ({ targetType, targetId, onSuccess, existingReview, onCancel }) => {
  // Review type selection
  const [reviewType, setReviewType] = useState(existingReview?.reviewType || 'quick');

  // Basic fields (backward compatible)
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState(existingReview?.comment || '');

  // NEW: Category ratings
  const [categoryRatings, setCategoryRatings] = useState({
    graphics: existingReview?.ratings?.graphics || 0,
    gameplay: existingReview?.ratings?.gameplay || 0,
    value: existingReview?.ratings?.value || 0,
    sound: existingReview?.ratings?.sound || 0,
    story: existingReview?.ratings?.story || 0,
    replayability: existingReview?.ratings?.replayability || 0
  });

  // NEW: Pros and cons
  const [pros, setPros] = useState(existingReview?.pros || []);
  const [cons, setCons] = useState(existingReview?.cons || []);
  const [newPro, setNewPro] = useState('');
  const [newCon, setNewCon] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existingReview) {
      setReviewType(existingReview.reviewType || 'quick');
      setRating(existingReview.rating);
      setComment(existingReview.comment);

      if (existingReview.ratings) {
        setCategoryRatings({
          graphics: existingReview.ratings.graphics || 0,
          gameplay: existingReview.ratings.gameplay || 0,
          value: existingReview.ratings.value || 0,
          sound: existingReview.ratings.sound || 0,
          story: existingReview.ratings.story || 0,
          replayability: existingReview.ratings.replayability || 0
        });
      }

      setPros(existingReview.pros || []);
      setCons(existingReview.cons || []);
    }
  }, [existingReview]);

  // Auto-calculate overall rating from category ratings if detailed review
  useEffect(() => {
    if (reviewType === 'detailed') {
      const ratings = Object.values(categoryRatings).filter(r => r > 0);
      if (ratings.length > 0) {
        const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
        setRating(Math.round(avg));
      }
    }
  }, [categoryRatings, reviewType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      setError('Please write a review');
      return;
    }

    if (comment.length > 2000) {
      setError('Review cannot exceed 2000 characters');
      return;
    }

    // Validate detailed review if selected
    if (reviewType === 'detailed') {
      const filledRatings = Object.values(categoryRatings).filter(r => r > 0);
      if (filledRatings.length === 0) {
        setError('Please provide at least one category rating for detailed reviews');
        return;
      }

      if (pros.length === 0 && cons.length === 0) {
        setError('Please add at least one pro or con for detailed reviews');
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const reviewData = {
        targetType,
        targetId,
        rating,
        comment,
        reviewType
      };

      // Add detailed fields if detailed review
      if (reviewType === 'detailed') {
        reviewData.ratings = categoryRatings;
        reviewData.pros = pros;
        reviewData.cons = cons;
      }

      if (existingReview) {
        // Update existing review
        await axios.put(
          `/api/reviews/update/${existingReview._id}`,
          reviewData,
          config
        );
      } else {
        // Create new review
        await axios.post(
          '/api/reviews/create',
          reviewData,
          config
        );
      }

      // Reset form
      setRating(0);
      setComment('');
      setReviewType('quick');
      setCategoryRatings({
        graphics: 0,
        gameplay: 0,
        value: 0,
        sound: 0,
        story: 0,
        replayability: 0
      });
      setPros([]);
      setCons([]);

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (value, onChange) => {
    return [1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className={`star-button ${star <= (hoveredRating || value) ? 'active' : ''}`}
        onClick={() => onChange(star)}
        onMouseEnter={() => setHoveredRating(star)}
        onMouseLeave={() => setHoveredRating(0)}
        disabled={loading}
      >
        ★
      </button>
    ));
  };

  const renderCategoryRating = (category, label) => {
    return (
      <div className="category-rating" key={category}>
        <label>{label}</label>
        <div className="star-rating-selector">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`star-button small ${star <= categoryRatings[category] ? 'active' : ''}`}
              onClick={() => setCategoryRatings({ ...categoryRatings, [category]: star })}
              disabled={loading}
            >
              ★
            </button>
          ))}
          <span className="rating-value">{categoryRatings[category] || '-'}</span>
        </div>
      </div>
    );
  };

  const addPro = () => {
    if (newPro.trim() && pros.length < 5) {
      setPros([...pros, newPro.trim()]);
      setNewPro('');
    }
  };

  const removePro = (index) => {
    setPros(pros.filter((_, i) => i !== index));
  };

  const addCon = () => {
    if (newCon.trim() && cons.length < 5) {
      setCons([...cons, newCon.trim()]);
      setNewCon('');
    }
  };

  const removeCon = (index) => {
    setCons(cons.filter((_, i) => i !== index));
  };

  return (
    <div className="review-form-container">
      <h3>{existingReview ? 'Edit Your Review' : 'Write a Review'}</h3>

      <form onSubmit={handleSubmit} className="review-form">
        {/* Review Type Selection */}
        {!existingReview && (
          <div className="form-group">
            <label>Review Type</label>
            <div className="review-type-selector">
              <button
                type="button"
                className={`btn ${reviewType === 'quick' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setReviewType('quick')}
                disabled={loading}
                style={{ marginRight: '12px' }}
              >
                ⚡ Quick Review
              </button>
              <button
                type="button"
                className={`btn ${reviewType === 'detailed' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setReviewType('detailed')}
                disabled={loading}
              >
                📝 Detailed Review
              </button>
            </div>
            <small className="help-text">
              {reviewType === 'quick' ? 'Quick review with overall rating and comment' : 'Detailed review with category ratings and pros/cons'}
            </small>
          </div>
        )}

        {/* Overall Rating (for quick reviews or manual override) */}
        {reviewType === 'quick' && (
          <div className="form-group">
            <label>Overall Rating</label>
            <div className="star-rating-selector">
              {renderStars(rating, setRating)}
              {rating > 0 && (
                <span className="rating-text">{rating} out of 5 stars</span>
              )}
            </div>
          </div>
        )}

        {/* Category Ratings (for detailed reviews) */}
        {reviewType === 'detailed' && (
          <div className="form-group">
            <label>Category Ratings</label>
            <div className="category-ratings-grid">
              {renderCategoryRating('graphics', '🎨 Graphics')}
              {renderCategoryRating('gameplay', '🎮 Gameplay')}
              {renderCategoryRating('value', '💰 Value')}
              {renderCategoryRating('sound', '🔊 Sound/Music')}
              {renderCategoryRating('story', '📖 Story')}
              {renderCategoryRating('replayability', '🔄 Replayability')}
            </div>
            <div className="overall-calculated">
              <strong>Overall: {rating}/5</strong>
              <small>(Auto-calculated from categories)</small>
            </div>
          </div>
        )}

        {/* Pros and Cons (for detailed reviews) */}
        {reviewType === 'detailed' && (
          <div className="pros-cons-section">
            <div className="form-group pros-group">
              <label>👍 Pros (up to 5)</label>
              <div className="pros-list">
                {pros.map((pro, index) => (
                  <div key={index} className="pro-item">
                    <span>+ {pro}</span>
                    <button
                      type="button"
                      onClick={() => removePro(index)}
                      className="remove-button"
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              {pros.length < 5 && (
                <div className="add-item-input">
                  <input
                    type="text"
                    value={newPro}
                    onChange={(e) => setNewPro(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPro())}
                    placeholder="Add a pro..."
                    maxLength="100"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={addPro}
                    disabled={!newPro.trim() || loading}
                    className="btn btn-primary btn-sm"
                    style={{ marginLeft: '8px' }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            <div className="form-group cons-group">
              <label>👎 Cons (up to 5)</label>
              <div className="cons-list">
                {cons.map((con, index) => (
                  <div key={index} className="con-item">
                    <span>− {con}</span>
                    <button
                      type="button"
                      onClick={() => removeCon(index)}
                      className="remove-button"
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              {cons.length < 5 && (
                <div className="add-item-input">
                  <input
                    type="text"
                    value={newCon}
                    onChange={(e) => setNewCon(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCon())}
                    placeholder="Add a con..."
                    maxLength="100"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={addCon}
                    disabled={!newCon.trim() || loading}
                    className="btn btn-primary btn-sm"
                    style={{ marginLeft: '8px' }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Review Text */}
        <div className="form-group">
          <label htmlFor="comment">Your Review</label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this..."
            rows="5"
            maxLength="2000"
            disabled={loading}
            required
          />
          <small className="character-count">
            {comment.length}/2000 characters
          </small>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>

          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>

        {!existingReview && (
          <p className="info-text">
            ℹ️ Your review will be visible after admin approval.
            {reviewType === 'detailed' && ' Detailed reviews earn bonus points!'}
          </p>
        )}

        {existingReview && (
          <p className="info-text">
            ℹ️ Updated reviews require admin approval before being displayed.
          </p>
        )}
      </form>
    </div>
  );
};

export default ReviewForm;
